import { useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import { Box, Flex } from '@enechain/ecloud-designsystem'

import { DateNav } from '~/components/balance/FilterCard/DateNav'
import { ToggleSwitch } from '~/components/balance/FilterCard/ToggleSwitch'
import { WEEKDAY_KEYS } from '~/components/common/DayGrid'
import {
  AreaFilter,
  CollapsibleFilterToolbar,
  FilterGrid,
  type FilterGridItem,
  type ToolbarStatusItem,
} from '~/components/common/filters'
import { GENERATION_LIST_SERIES_COLORS } from '~/constants/chartPalette'
import { useGenerationSourceList } from '~/hooks/useGenerationSourceList'
import { convertAreaLabel } from '~/models/area'
import { formatDateDisplay } from '~/models/dateTime'
import { StackOrderModalButton } from '~/pages/BalanceGeneration/components/StackOrderModalButton'
import type { GenSeriesKey } from '~/pages/BalanceGeneration/constants'
import type { UseGenFilterStateReturn } from '~/pages/BalanceGeneration/hooks/useFilterState'

type Props = {
  filter: UseGenFilterStateReturn
}

/** 開閉の保存キー (CollapsibleFilterToolbar が接頭辞を足す)。 */
export const GENERATION_FILTER_STORAGE_KEY = 'balanceGeneration'

/** 選んだものが 1 つも無い時の要約の値 (空白にしない)。 */
const EMPTY_SUMMARY_VALUE = '—'

type ToggleItem = {
  key: GenSeriesKey
  labelKey: string
  /** ON 時のトラック色 = チャートの系列色 (design `.switch-item[data-color]`)。 */
  color: string
}

// 色は [[GENERATION_LIST_SERIES_COLORS]] から引く。
// switch とチャートで別々に色を書くと、 片方だけ変えた時に凡例として嘘になるため
// (需要側 FilterCard と同じ作法)。
const BASIC_TOGGLES: ToggleItem[] = [
  {
    key: 'genPlan',
    labelKey: 'page:balanceGeneration.filter.genPlan',
    color: GENERATION_LIST_SERIES_COLORS.genPlan,
  },
  {
    key: 'genActual',
    labelKey: 'page:balanceGeneration.filter.genActual',
    color: GENERATION_LIST_SERIES_COLORS.genActual,
  },
]

// 発電形式のトグルは並び順・色・ラベルを 9 区分の SoT からそのまま導出する
// (追加定義をここに持たない)。
//
// [#7058 / AC-SPS-007・008] 並び順と色は**個人設定の永続値**なので、module
// スコープの const ではなく hook から取る。 const だと module 読み込み時に
// 既定値で固まり、 設定を変えても凡例が追随しない (= 凡例が嘘になる)。
// チャート (BgChart) と同じ hook を読むので、両者の順・色は常に一致する。
//
// 開閉式 (UI 辞書 toolbar-collapsible.md「開閉式」)。 選ぶ部品はすべて開いたカードに置き、
// 閉じている間は要約で今の値を読む。 カードの並びは エリア → 表示日 → 基本表示 → 発電形式内訳。
// - 件数・クリア = エリア・基本表示 (計画値 / 実績値)・発電形式内訳 の 3 条件。 表示日は数えず戻さない
// - 「レイアウトリセット」はフィルターではない (グラフ配置) ので、 カードの外・グラフの上 (index.tsx)
// - 「積み上げ順・色」は発電形式内訳の編集導線なので、 その行 (カードの中) に置く (#7058)
//
// [UI 辞書との差] 辞書の発電行にある「次の GC のカウントダウン (trailing)」「電源区分」「コマ表示単位」は、
// dev の発電一覧に対応するデータ・条件がまだ無い (一覧のレスポンスに GC の締切が無い) ので持たない。
export const FilterCard = ({ filter }: Props): React.ReactElement => {
  const { t, i18n } = useTranslation()
  const sourceList = useGenerationSourceList()
  const sourceToggles: ToggleItem[] = useMemo(
    () =>
      sourceList.map((item) => ({
        key: item.key,
        labelKey: item.labelKey,
        color: item.color,
      })),
    [sourceList],
  )

  const renderToggle = (item: ToggleItem): React.ReactElement => (
    <ToggleSwitch
      key={item.key}
      isOn={filter.seriesVisibility[item.key]}
      label={t(item.labelKey)}
      onColor={item.color}
      onToggle={() => filter.toggleSeries(item.key)}
    />
  )

  // --- 要約と件数 (既定 = クリアで戻る値: 全エリア・全系列 ON) ---
  const all = t('page:balanceGeneration.filter.allAreas')
  const separator = t('common:filter.listSeparator')
  const isAllAreas = filter.selectableAreaCodes.every((code) =>
    filter.visibleAreaCodes.has(code),
  )
  const areaValue = isAllAreas
    ? all
    : filter.selectableAreaCodes
        .filter((code) => filter.visibleAreaCodes.has(code))
        .map((code) => convertAreaLabel(code))
        .join(separator) || EMPTY_SUMMARY_VALUE

  /** 系列の組の要約。 全部 ON = 「すべて」、 一部 = ON のものの名前、 0 件 = 「—」。 */
  const seriesSummary = (items: readonly ToggleItem[]): string => {
    const on = items.filter((item) => filter.seriesVisibility[item.key])
    if (on.length === items.length) return all
    return (
      on.map((item) => t(item.labelKey)).join(separator) || EMPTY_SUMMARY_VALUE
    )
  }
  const isSeriesChanged = (items: readonly ToggleItem[]): boolean =>
    items.some((item) => !filter.seriesVisibility[item.key])

  const areaLabel = t('page:balanceGeneration.filter.displayArea')
  const dateLabel = t('page:balanceGeneration.filter.displayDate')
  const basicLabel = t('page:balanceGeneration.filter.basicSeries')
  const sourceLabel = t('page:balanceGeneration.filter.sourceBreakdown')
  const summary: ToolbarStatusItem[] = [
    { key: 'area', label: areaLabel, value: areaValue },
    {
      key: 'date',
      label: dateLabel,
      // 日付欄の閉じた表示と同じ `YYYY/MM/DD (曜)`
      value: t('common:date.withWeekday', {
        date: formatDateDisplay(filter.displayDate, i18n.language),
        weekday: t(
          `common:date.calendar.weekday.${WEEKDAY_KEYS[filter.displayDate.getDay()]}`,
        ),
      }),
    },
    // 見え方の設定は既定 (すべて表示) から変えた群だけ出す (UI 辞書 toolbar-collapsible.md の要約。 需要と同じ)
    ...(isSeriesChanged(BASIC_TOGGLES)
      ? [
          {
            key: 'basicSeries',
            label: basicLabel,
            value: seriesSummary(BASIC_TOGGLES),
          },
        ]
      : []),
    ...(isSeriesChanged(sourceToggles)
      ? [
          {
            key: 'sourceBreakdown',
            label: sourceLabel,
            value: seriesSummary(sourceToggles),
          },
        ]
      : []),
  ]
  const modifiedCount = [
    !isAllAreas,
    isSeriesChanged(BASIC_TOGGLES),
    isSeriesChanged(sourceToggles),
  ].filter(Boolean).length

  const items: FilterGridItem[] = [
    {
      key: 'area',
      label: areaLabel,
      // エリア選択 (doji-hatsu.md AC-BDH-004/005/012, issue #7536): 需要側 FilterCard と同じ
      // checkbox マルチセレクト + 全選択/全解除のマスターチェック。 選択肢
      // (`filter.selectableAreaCodes`) は呼び出し元 `BalanceGenerationPage` が対象エリア
      // (全 10 エリア) ∩ 業務エリア設定で計算する (issue #7535、conventions.md「業務エリアフィルタの原則」)。
      control: (
        <AreaFilter
          allLabel={all}
          areaCodes={filter.selectableAreaCodes}
          onChange={filter.setVisibleAreaCodes}
          value={filter.visibleAreaCodes}
          variant="checkbox"
        />
      ),
    },
    {
      key: 'date',
      // ラベルは DateNav が持つ (横並び)
      control: (
        <DateNav
          displayDate={filter.displayDate}
          labelKeyPrefix="page:balanceGeneration.filter"
          layout="horizontal"
          onDateChange={filter.setDisplayDate}
          onToday={filter.goToToday}
        />
      ),
    },
    {
      key: 'basicSeries',
      label: basicLabel,
      control: (
        <Flex alignItems="center" flexWrap="wrap" gap={3}>
          {BASIC_TOGGLES.map((item) => renderToggle(item))}
        </Flex>
      ),
    },
    {
      key: 'sourceBreakdown',
      label: sourceLabel,
      control: (
        <Flex alignItems="center" flexWrap="wrap" gap={3}>
          {sourceToggles.map((item) => renderToggle(item))}
          {/* [#7058] 「積み上げ順・色」は発電形式内訳の行に置く (2026-10-02 デザイナー判断)。
              行の見出しで何の積み上げ順・色かが分かり、トグルと同じ場所で見え方を操作できる。 */}
          <StackOrderModalButton />
        </Flex>
      ),
    },
  ]

  return (
    <Box flexShrink={0} marginBottom="2px">
      <CollapsibleFilterToolbar
        modifiedCount={modifiedCount}
        // 表示日は戻さない。 フィルター (エリア・系列トグル) だけを初期値へ戻す。
        // [design差異] 8080 (`balance/hatsu.html` の `#layout-reset-btn`) は同じ位置の「クリア」1 つで
        // フィルタとグラフ配置を両方戻す。 2026-09-07 のデザイナー判断で 2 つに分け、
        // グラフ配置は チャートの上・右端の「レイアウトリセット」が持つ (需要側と同じ)。
        onClear={filter.clearFilters}
        storageKey={GENERATION_FILTER_STORAGE_KEY}
        summary={summary}
      >
        <FilterGrid items={items} />
      </CollapsibleFilterToolbar>
    </Box>
  )
}
